import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionStorageValue } from '@/shared/lib/storage';
import { Button, Dialog, Separator, Switch, Text } from '@/shared/ui';
import { useAccuracyCheckMode } from '../model/useAccuracyCheckMode';
import { useAITokenErrorMode } from '../model/useAITokenErrorMode';
import { useBillingPeriodTypeOverride } from '../model/useBillingPeriodTypeOverride';
import { useEmptyMode } from '../model/useEmptyMode';
import { usePrefecturalGrantsOverride } from '../model/usePrefecturalGrantsOverride';
import { usePrefecturalSpecialAllocationTaxesOverride } from '../model/usePrefecturalSpecialAllocationTaxesOverride';
import { useRoleOverride } from '../model/useRoleOverride';
import { useTokenUsageOverride } from '../model/useTokenUsageOverride';

/**
 * UI制御ダイアログ
 * 開発環境でのUI制御設定を行うダイアログ。
 *
 * 項目は用途別に4カテゴリへ分割している:
 * - データ表示: 空データモード / 精度確認カード
 * - AI機能・プラン: AIトークンエラー / トークン使用率 / 契約期間タイプ
 * - マッチング機能: 都道府県補助金 / 特別交付税（調査票）
 * - ロール・権限: ユーザーロール
 */
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ToggleSetting = {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

// オーバーライド系セレクトの共通 props
type OverrideButtonGroupProps<T extends string> = {
  label: string;
  description?: string;
  options: readonly T[];
  optionLabels: Record<T, string>;
  value: T;
  onChange: (value: T) => void;
  /** ロール上書きのように選択値で補足説明を出したい場合に渡す */
  trailingDescription?: string;
};

/**
 * 'default' | xxx | yyy を切り替える button select を描画する共通サブコンポーネント。
 * UIControlDialog 内のオーバーライド系セクションの重複を避けるためファイル内に局所定義する。
 */
const OverrideButtonGroup = <T extends string>({
  label,
  description,
  options,
  optionLabels,
  value,
  onChange,
  trailingDescription,
}: OverrideButtonGroupProps<T>) => (
  <div className="space-y-2">
    <Text>{label}</Text>
    {description && <Text variant="description">{description}</Text>}
    <div className="flex gap-2">
      {options.map((option) => (
        <Button
          key={option}
          size="sm"
          variant={value === option ? 'primary' : 'outline'}
          onClick={() => onChange(option)}
        >
          {optionLabels[option]}
        </Button>
      ))}
    </div>
    {trailingDescription && (
      <Text variant="description" className="whitespace-pre-line">
        {trailingDescription}
      </Text>
    )}
  </div>
);

type PrefecturalGrantsOverrideValue = SessionStorageValue<'debug-prefectural-grants-override'>;
type PrefecturalSpecialAllocationTaxesOverrideValue =
  SessionStorageValue<'debug-prefectural-special-allocation-taxes-override'>;
type RoleOverrideValue = SessionStorageValue<'debug-role-override'>;
type BillingPeriodTypeOverrideValue = SessionStorageValue<'debug-billing-period-type-override'>;

const ENABLE_DISABLE_OVERRIDE_OPTIONS = ['default', 'enabled', 'disabled'] as const;
const ROLE_OVERRIDE_OPTIONS: readonly RoleOverrideValue[] = ['default', 'admin', 'editor'];
const BILLING_PERIOD_TYPE_OVERRIDE_OPTIONS: readonly BillingPeriodTypeOverrideValue[] = [
  'default',
  'monthly',
  'yearly',
];

// セクション見出し共通スタイル
const SectionHeading = ({ children }: { children: string }) => (
  <Text variant="subhead">{children}</Text>
);

export const UIControlDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const { isEmptyMode, toggleEmptyMode } = useEmptyMode();
  const { isAccuracyCheckMode, toggleAccuracyCheckMode } = useAccuracyCheckMode();
  const { isAITokenErrorMode, toggleAITokenErrorMode } = useAITokenErrorMode();
  const { overridePercentage, updateOverridePercentage } = useTokenUsageOverride();
  const { overrideValue: prefecturalOverride, updateOverrideValue: updatePrefecturalOverride } =
    usePrefecturalGrantsOverride();
  const {
    overrideValue: prefecturalSpecialTaxOverride,
    updateOverrideValue: updatePrefecturalSpecialTaxOverride,
  } = usePrefecturalSpecialAllocationTaxesOverride();
  const { overrideValue: roleOverride, updateOverrideValue: updateRoleOverride } =
    useRoleOverride();
  const {
    overrideValue: billingPeriodTypeOverride,
    updateOverrideValue: updateBillingPeriodTypeOverride,
  } = useBillingPeriodTypeOverride();

  const [emptyModeChecked, setEmptyModeChecked] = useState<boolean>(isEmptyMode);
  const [accuracyCheckChecked, setAccuracyCheckChecked] = useState<boolean>(isAccuracyCheckMode);
  const [aiTokenErrorChecked, setAiTokenErrorChecked] = useState<boolean>(isAITokenErrorMode);
  const [prefecturalGrantsValue, setPrefecturalGrantsValue] =
    useState<PrefecturalGrantsOverrideValue>(prefecturalOverride);
  const [prefecturalSpecialTaxValue, setPrefecturalSpecialTaxValue] =
    useState<PrefecturalSpecialAllocationTaxesOverrideValue>(prefecturalSpecialTaxOverride);
  const [roleOverrideValue, setRoleOverrideValue] = useState<RoleOverrideValue>(roleOverride);
  const [billingPeriodTypeValue, setBillingPeriodTypeValue] =
    useState<BillingPeriodTypeOverrideValue>(billingPeriodTypeOverride);
  const [tokenUsageEnabled, setTokenUsageEnabled] = useState<boolean>(overridePercentage !== null);
  const [tokenUsageValue, setTokenUsageValue] = useState<number>(overridePercentage ?? 50);

  // 動的キー生成禁止ルールに沿って明示マッピング
  // 「デフォルト（API準拠）」は debug-menu.override-common.option-api-default に共通化
  const apiDefaultLabel = t('debug-menu.override-common.option-api-default');
  const enableDisableLabels = {
    default: apiDefaultLabel,
    enabled: t('debug-menu.prefectural-grants-override.option-enabled'),
    disabled: t('debug-menu.prefectural-grants-override.option-disabled'),
  };
  const prefecturalSpecialTaxLabels = {
    default: apiDefaultLabel,
    enabled: t('debug-menu.prefectural-special-allocation-taxes-override.option-enabled'),
    disabled: t('debug-menu.prefectural-special-allocation-taxes-override.option-disabled'),
  };
  const roleOverrideLabels = {
    default: apiDefaultLabel,
    admin: t('debug-menu.role-override.option-admin'),
    editor: t('debug-menu.role-override.option-editor'),
  };
  const billingPeriodTypeLabels = {
    default: apiDefaultLabel,
    monthly: t('debug-menu.billing-period-type-override.option-monthly'),
    yearly: t('debug-menu.billing-period-type-override.option-yearly'),
  };

  const roleCapabilityDescription =
    roleOverrideValue === 'admin'
      ? t('debug-menu.role-override.capabilities-admin')
      : roleOverrideValue === 'editor'
        ? t('debug-menu.role-override.capabilities-editor')
        : undefined;

  const dataDisplayToggles: ToggleSetting[] = [
    {
      label: t('debug-menu.empty-mode.label'),
      description: t('debug-menu.empty-mode.description'),
      checked: emptyModeChecked,
      onChange: setEmptyModeChecked,
    },
    {
      label: t('debug-menu.accuracy-check-mode.label'),
      description: t('debug-menu.accuracy-check-mode.description'),
      checked: accuracyCheckChecked,
      onChange: setAccuracyCheckChecked,
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={() => {
        toggleEmptyMode(emptyModeChecked);
        toggleAccuracyCheckMode(accuracyCheckChecked);
        toggleAITokenErrorMode(aiTokenErrorChecked);
        updatePrefecturalOverride(prefecturalGrantsValue);
        updatePrefecturalSpecialTaxOverride(prefecturalSpecialTaxValue);
        updateRoleOverride(roleOverrideValue);
        updateBillingPeriodTypeOverride(billingPeriodTypeValue);
        updateOverridePercentage(tokenUsageEnabled ? tokenUsageValue : null);
        window.location.reload();
        onOpenChange(false);
      }}
      confirmLabel={t('debug-menu.apply')}
      title={t('debug-menu.ui-control')}
    >
      <div className="space-y-4">
        <div>
          <Text variant="body">{t('debug-menu.session-storage-note')}</Text>
        </div>

        <div className="-mr-2 max-h-[60vh] space-y-6 overflow-y-auto border-t border-b py-4 pr-2">
          {/* セクション1: データ表示 */}
          <section className="space-y-3">
            <SectionHeading>{t('debug-menu.section.data-display')}</SectionHeading>
            {dataDisplayToggles.map((setting) => (
              <div key={setting.label} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Text>{setting.label}</Text>
                  <Switch checked={setting.checked} onCheckedChange={setting.onChange} />
                </div>
                {setting.description && <Text variant="description">{setting.description}</Text>}
              </div>
            ))}
          </section>

          <Separator />

          {/* セクション2: AI機能・プラン */}
          <section className="space-y-3">
            <SectionHeading>{t('debug-menu.section.ai-and-plan')}</SectionHeading>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Text>{t('debug-menu.ai-token-error-mode.label')}</Text>
                <Switch checked={aiTokenErrorChecked} onCheckedChange={setAiTokenErrorChecked} />
              </div>
              <Text variant="description">{t('debug-menu.ai-token-error-mode.description')}</Text>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Text>{t('debug-menu.token-usage-override.label')}</Text>
                <Switch checked={tokenUsageEnabled} onCheckedChange={setTokenUsageEnabled} />
              </div>
              <Text variant="description">{t('debug-menu.token-usage-override.description')}</Text>
              {tokenUsageEnabled && (
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={120}
                    value={tokenUsageValue}
                    onChange={(e) => setTokenUsageValue(Number(e.target.value))}
                    className="flex-1"
                  />
                  <Text className="w-12 text-right">{tokenUsageValue}%</Text>
                </div>
              )}
            </div>

            <OverrideButtonGroup
              label={t('debug-menu.billing-period-type-override.label')}
              description={t('debug-menu.billing-period-type-override.description')}
              options={BILLING_PERIOD_TYPE_OVERRIDE_OPTIONS}
              optionLabels={billingPeriodTypeLabels}
              value={billingPeriodTypeValue}
              onChange={setBillingPeriodTypeValue}
            />
          </section>

          <Separator />

          {/* セクション3: マッチング機能 */}
          <section className="space-y-3">
            <SectionHeading>{t('debug-menu.section.matching')}</SectionHeading>
            <OverrideButtonGroup
              label={t('debug-menu.prefectural-grants-override.label')}
              description={t('debug-menu.prefectural-grants-override.description')}
              options={ENABLE_DISABLE_OVERRIDE_OPTIONS}
              optionLabels={enableDisableLabels}
              value={prefecturalGrantsValue}
              onChange={setPrefecturalGrantsValue}
            />
            <OverrideButtonGroup
              label={t('debug-menu.prefectural-special-allocation-taxes-override.label')}
              description={t(
                'debug-menu.prefectural-special-allocation-taxes-override.description',
              )}
              options={ENABLE_DISABLE_OVERRIDE_OPTIONS}
              optionLabels={prefecturalSpecialTaxLabels}
              value={prefecturalSpecialTaxValue}
              onChange={setPrefecturalSpecialTaxValue}
            />
          </section>

          <Separator />

          {/* セクション4: ロール・権限 */}
          <section className="space-y-3">
            <SectionHeading>{t('debug-menu.section.role')}</SectionHeading>
            <OverrideButtonGroup
              label={t('debug-menu.role-override.label')}
              description={t('debug-menu.role-override.description')}
              options={ROLE_OVERRIDE_OPTIONS}
              optionLabels={roleOverrideLabels}
              value={roleOverrideValue}
              onChange={setRoleOverrideValue}
              trailingDescription={roleCapabilityDescription}
            />
          </section>
        </div>
      </div>
    </Dialog>
  );
};
